import React, {ReactNode, useCallback, useEffect} from 'react';
import {some, isEmpty, isNil, sortBy, map} from 'lodash';
import {useDispatch, useSelector} from 'react-redux';
import classNames from 'classnames';

import {newHonorAward, editHonorAward} from 'shared/portfolio/actions/honor-awards';
import {getHonorAwards} from 'src/redux/lists';
import {EducationPhase} from 'src/types/enums';
import {useMediaQuery} from 'src/hooks/useMediaQuery';
import {Store} from 'src/types/store';

import TwoColumnMasonry from 'shared/portfolio/components/two-column-masonry';
import Card from '../card';
import Section from '../section';
import HonorAwardsFormCard from 'shared/portfolio/components/honor-awards/honor-awards-form-card';
import HonorAward from './honor-award';
import PortfolioSectionHeader from '../portfolio-section-header';
import EmptyPlaceholder from '../empty-placeholder';
import PortfolioIcon from '../portfolio-icon/portfolio-icon';

const HonorAwards = () => {
  const mq = useMediaQuery();
  const dispatch = useDispatch();
  const honorAwards = useSelector((state: Store) => state.honorAwards);
  const transfer = useSelector((state: Store) => state.transfer);
  const editing = some(honorAwards, 'editing');
  const handleAddClick = useCallback(() => dispatch(newHonorAward()), [dispatch]);

  useEffect(() => {
    window.Raise.vent.on('add:honorAward', handleAddClick);
  }, [handleAddClick]);

  useEffect(() => {
    dispatch(
      getHonorAwards(transfer ? EducationPhase.COMMUNITY_COLLEGE : EducationPhase.HIGH_SCHOOL)
    );
  }, [dispatch, transfer]);

  let honorAwardItems: ReactNode = null;

  if (isEmpty(honorAwards)) {
    honorAwardItems = (
      <div className="portfolio-sections column">
        <EmptyPlaceholder
          onClick={handleAddClick}
          header="Add an Honor or Award"
          description="Up to $150 each"
          portfolioItemType="honor-awards"
        />
      </div>
    );
  } else {
    const sorted = sortBy(honorAwards, (honorAward) => {
      if (isNil(honorAward.year) || isNil(honorAward.month)) {
        return Number.MAX_SAFE_INTEGER;
      }

      return honorAward.year * 100 + honorAward.month;
    });

    sorted.reverse();

    honorAwardItems = (
      <TwoColumnMasonry>
        {map(sorted, (honorAward) => {
          if (honorAward.editing) {
            return (
              <div
                className={classNames('portfolio-sections column', {
                  editing: honorAward.editing,
                })}
                key={honorAward._id}
              >
                <HonorAwardsFormCard honorAward={honorAward} />
              </div>
            );
          } else {
            return (
              <div className="portfolio-sections column" key={honorAward._id}>
                <Card
                  id={honorAward._id}
                  active={!!honorAward.scrollTo}
                  onClick={() => {
                    dispatch(editHonorAward(honorAward));
                  }}
                >
                  <HonorAward honorAward={honorAward} />
                </Card>
              </div>
            );
          }
        })}
      </TwoColumnMasonry>
    );
  }

  return (
    <div>
      <PortfolioSectionHeader
        title="Honors & Awards"
        displayNew={!editing && !(mq.XS && !Object.keys(honorAwards).length)}
        addNew={handleAddClick}
        sectionName="honor-awards"
      >
        <PortfolioIcon type="honorAwards" size={mq.XS ? 40 : 56} />
      </PortfolioSectionHeader>
      <Section>
        <div id="honors-and-awards" className="portfolio-sections main-section"></div>
        {honorAwardItems}
      </Section>
    </div>
  );
};

export default HonorAwards;
